import { motion } from "framer-motion";
import { CheckCircle2, Copy, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Vinci Service Request Number — case-file acknowledgement panel.
 *
 * Renders after any user-originated interaction (RFQ, KYC, Claim,
 * Concierge message, etc.) with the SRN pulsing in gold monospace,
 * a "CASE FILE OPENED" bureaucratic header, and an optional stack of
 * summary rows. Designed to feel like a government intake receipt.
 *
 * Props:
 *   - srn (string, required):  "VNC-SRN-YYYY-MMDD-XXXX"
 *   - title (string):          headline copy above the SRN
 *   - subtitle (string):       explanatory line under the title
 *   - kind (string):           short kind label e.g. "Quote Request"
 *   - summary ([{k,v}] | array of [k,v]):  detail rows shown as a grid
 *   - actions (ReactNode):     right-side CTA row (optional)
 *   - vinciCode (string):      partner's VN-5151 code (optional footer)
 */
export function SRNAcknowledgement({
  srn,
  title = "Request Acknowledged",
  subtitle = "Your case file has been opened on the Vinci Master Ledger.",
  kind = "Service Request",
  summary = [],
  actions = null,
  vinciCode = null,
}) {
  const rows = Array.isArray(summary)
    ? summary.map((r) => (Array.isArray(r) ? { k: r[0], v: r[1] } : r))
    : [];

  const copySrn = () => {
    if (!srn) return;
    navigator.clipboard.writeText(srn);
    toast.success("SRN copied to clipboard.");
  };

  return (
    <motion.div
      data-testid="srn-acknowledgement"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-sm border border-[#C9A961]/45 bg-gradient-to-b from-[#0e0d0a] via-[#0a0908] to-[#0e0d0a] p-8 md:p-12"
    >
      {/* Subtle guilloche corners */}
      <span aria-hidden className="absolute -top-6 -left-6 h-28 w-28 rounded-full border border-[#C9A961]/15" />
      <span aria-hidden className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full border border-[#C9A961]/10" />

      {/* Case-file header */}
      <div className="flex items-center justify-between gap-6 border-b border-[#C9A961]/25 pb-5">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-full bg-[#C9A961] text-black">
            <CheckCircle2 size={18} />
          </span>
          <div>
            <div className="font-grotesk text-[9.5px] uppercase tracking-[0.32em] text-[#C9A961]">
              · Vinci Trade Desk · Case File Opened ·
            </div>
            <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">
              {title}
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 rounded-full border border-[#C9A961]/40 px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#C9A961]">
          <FileText size={12} /> {kind}
        </div>
      </div>

      {/* SRN monolith */}
      <div className="mt-8 mb-2 flex flex-col items-center">
        <div className="font-grotesk text-[9px] uppercase tracking-[0.36em] text-[#8A8A82]">
          Service Request Number
        </div>
        <div className="relative mt-4">
          <motion.div
            data-testid="srn-value"
            animate={{
              textShadow: [
                "0 0 0px rgba(201,169,97,0)",
                "0 0 22px rgba(201,169,97,0.55)",
                "0 0 0px rgba(201,169,97,0)",
              ],
            }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            style={{
              fontFamily:
                'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            }}
            className="text-[#C9A961] font-bold text-2xl md:text-4xl tracking-[0.14em] select-all"
          >
            {srn}
          </motion.div>
        </div>
        <button
          type="button"
          onClick={copySrn}
          data-testid="srn-copy"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#C9A961]/40 px-4 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961] hover:bg-[#C9A961]/10 transition-colors"
        >
          <Copy size={12} /> Copy SRN
        </button>
      </div>

      {/* Subtitle */}
      <p className="text-center font-body text-sm text-[#8A8A82] mt-5 max-w-xl mx-auto leading-relaxed">
        {subtitle}
        <br />
        Every update we send you will carry this SRN in the email subject line.
      </p>

      {/* Summary grid */}
      {rows.length > 0 && (
        <div className="mt-9 grid sm:grid-cols-2 gap-x-8 gap-y-4 font-body text-sm">
          {rows.map((r, i) => (
            <div key={`${r.k}-${i}`} className="flex flex-col">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#8A8A82]">
                {r.k}
              </span>
              <span className="text-[#C9C9C2] mt-1">{r.v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-[#C9A961]/25 pt-5 text-[10px] font-grotesk uppercase tracking-[0.24em] text-[#6f6f68]">
        <div>
          Ledger Sync · <span className="text-[#C9A961]">Vinci Master Ledger</span>
          {vinciCode ? (
            <>
              {" "}·{" "}
              <span className="text-[#C9A961]">VINCI CODE {vinciCode}</span>
            </>
          ) : null}
        </div>
        <div>Veni · Vidi · Vici</div>
      </div>

      {actions && <div className="mt-8 flex flex-wrap gap-4 justify-center">{actions}</div>}
    </motion.div>
  );
}
